import * as React from 'react';
import {
  BadgeCheck,
  Building2,
  Check,
  Printer,
  Save,
  Search,
  ShieldCheck,
  UserPlus,
  Warehouse,
  X,
} from 'lucide-react';
import {
  useAcessosWms,
  useBuscarUsuariosPbs,
  useCrachas,
  usePapeis,
  useSalvarAcesso,
  type AcessoUsuario,
  type CrachaPbs,
} from '@/hooks/admin';
import { useContexto } from '@/hooks/useContexto';
import { useDebounce } from '@/hooks/useDebounce';
import { useSessao } from '@/hooks/useSessao';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoCartao,
  Campo,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';

/**
 * Quem entra no WMS, e enxergando o quê.
 *
 * A base de usuários é a do PBS: aqui não se cria pessoa, se libera pessoa.
 * Buscar pelo nome e clicar já abre o formulário com o acesso atual — liberar
 * alguém novo e editar alguém que já tem acesso são o mesmo gesto, porque do
 * ponto de vista de quem administra é a mesma decisão.
 *
 * A concessão principal é a EMPRESA, não o centro: é assim que a operação fala
 * ("esse usuário vê a 11 e a 50"), e marcar uma empresa já libera os 23 CDs
 * dela. Centro avulso existe para o caso fino e fica recolhido, porque é a
 * exceção.
 */
export function Acessos(): JSX.Element {
  const { usuario: eu } = useSessao();
  const { centros, empresas } = useContexto();

  const [termo, definirTermo] = React.useState('');
  const termoAtrasado = useDebounce(termo, 300);
  const buscaPbs = useBuscarUsuariosPbs(termoAtrasado);
  const acessos = useAcessosWms();
  const papeis = usePapeis();
  const crachas = useCrachas();
  const salvar = useSalvarAcesso();

  const [editando, definirEditando] = React.useState<number | null>(null);

  const emEdicao = React.useMemo<AcessoUsuario | null>(() => {
    if (editando === null) return null;
    const existente = acessos.data?.find((a) => a.usuario === editando);
    if (existente) return existente;
    // Usuário do PBS ainda sem acesso: o formulário abre em branco, negando
    // tudo — é o estado real de quem nunca foi liberado. O crachá é o que o
    // Procfit já tiver vinculado (só leitura).
    const candidato = buscaPbs.data?.find((u) => u.usuario === editando);
    if (!candidato) return null;
    return {
      usuario: candidato.usuario,
      login: candidato.login,
      nome: candidato.nome,
      ativo: true,
      adminMaster: false,
      papeis: [],
      permissoes: [],
      empresas: [],
      centros: [],
      cracha: crachas.data?.find((c) => c.usuario === candidato.usuario)?.cracha ?? null,
    };
  }, [editando, acessos.data, buscaPbs.data, crachas.data]);

  return (
    <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <Cartao>
        <CabecalhoCartao>
          <TituloCartao>Usuários</TituloCartao>
          {acessos.data ? <Pilula tom="neutro">{acessos.data.length} com acesso</Pilula> : null}
        </CabecalhoCartao>
        <CorpoCartao className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoFraco" />
            <Campo
              value={termo}
              onChange={(e) => definirTermo(e.target.value)}
              placeholder="buscar no PBS por nome ou login"
              aria-label="Buscar usuário no PBS"
              className="pl-9"
            />
          </div>

          {/* Enquanto não se busca nada, a lista é a de quem já tem acesso —
              que é o que se quer ver ao abrir a tela. */}
          {termoAtrasado.trim().length < 2 ? (
            acessos.isLoading ? (
              <Esqueleto className="h-24" />
            ) : acessos.isError ? (
              <EstadoErro erro={acessos.error} aoTentarDeNovo={() => void acessos.refetch()} />
            ) : (acessos.data?.length ?? 0) === 0 ? (
              <EstadoVazio
                icone={<UserPlus className="h-7 w-7" />}
                titulo="Ninguém cadastrado ainda"
                detalhe="Busque pelo nome no PBS para liberar o primeiro acesso."
              />
            ) : (
              <ul className="divide-y divide-borda">
                {acessos.data?.map((a) => (
                  <LinhaUsuario
                    key={a.usuario}
                    nome={a.nome}
                    login={a.login}
                    ativo={editando === a.usuario}
                    aoAbrir={() => definirEditando(a.usuario)}
                    selos={
                      <>
                        {!a.ativo ? <Pilula tom="erro">inativo</Pilula> : null}
                        {a.adminMaster ? <Pilula tom="marca">admin</Pilula> : null}
                        {a.cracha ? (
                          <Pilula tom="neutro">crachá {a.cracha}</Pilula>
                        ) : null}
                        {a.empresas.length > 0 ? (
                          <Pilula tom="neutro">
                            {a.empresas.length} {a.empresas.length === 1 ? 'empresa' : 'empresas'}
                          </Pilula>
                        ) : (
                          <Pilula tom="alerta">sem empresa</Pilula>
                        )}
                      </>
                    }
                  />
                ))}
              </ul>
            )
          ) : buscaPbs.isLoading ? (
            <Esqueleto className="h-24" />
          ) : buscaPbs.isError ? (
            <EstadoErro erro={buscaPbs.error} />
          ) : (buscaPbs.data?.length ?? 0) === 0 ? (
            <EstadoVazio titulo={`Ninguém no PBS com “${termoAtrasado}”`} />
          ) : (
            <ul className="divide-y divide-borda">
              {buscaPbs.data?.map((u) => (
                <LinhaUsuario
                  key={u.usuario}
                  nome={u.nome}
                  login={u.login}
                  ativo={editando === u.usuario}
                  aoAbrir={() => definirEditando(u.usuario)}
                  selos={
                    u.liberado ? (
                      <Pilula tom="ok">já liberado</Pilula>
                    ) : (
                      <Pilula tom="neutro">sem acesso</Pilula>
                    )
                  }
                />
              ))}
            </ul>
          )}
        </CorpoCartao>
      </Cartao>

      {emEdicao ? (
        <Formulario
          key={emEdicao.usuario}
          acesso={emEdicao}
          empresas={empresas}
          centros={centros}
          papeis={papeis.data ?? []}
          crachas={crachas.data ?? []}
          souEu={eu?.usuario === emEdicao.usuario}
          salvando={salvar.isPending}
          erro={salvar.error}
          aoSalvar={(dados) => salvar.mutate(dados)}
          aoFechar={() => {
            salvar.reset();
            definirEditando(null);
          }}
        />
      ) : (
        <Cartao className="grid min-h-[240px] place-items-center">
          <EstadoVazio
            icone={<ShieldCheck className="h-7 w-7" />}
            titulo="Escolha um usuário"
            detalhe="Clique em alguém da lista para ver e mudar o que essa pessoa enxerga."
          />
        </Cartao>
      )}
    </div>
  );
}

function LinhaUsuario({
  nome,
  login,
  selos,
  ativo,
  aoAbrir,
}: {
  nome: string;
  login: string;
  selos: React.ReactNode;
  ativo: boolean;
  aoAbrir: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={aoAbrir}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left transition-colors',
          ativo ? 'bg-primaria-suave' : 'hover:bg-superficie2',
        )}
      >
        <span className="min-w-0">
          <span
            className={cn('block truncate text-[13.5px] font-semibold', ativo && 'text-primaria')}
          >
            {nome}
          </span>
          <span className="block truncate font-mono text-[11px] text-textoFraco">{login}</span>
        </span>
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">{selos}</span>
      </button>
    </li>
  );
}

/**
 * O formulário de um acesso.
 *
 * Estado local e salvamento explícito: mexer em acesso não pode ser
 * auto-salvo a cada clique — quem está montando uma permissão precisa poder
 * desistir no meio.
 */
function Formulario({
  acesso,
  empresas,
  centros,
  papeis,
  crachas,
  souEu,
  salvando,
  erro,
  aoSalvar,
  aoFechar,
}: {
  acesso: AcessoUsuario;
  empresas: { empresa: number; nome: string | null; centros: number }[];
  centros: { centroEstoque: number; nome: string; empresa: number }[];
  papeis: { papelId: number; nome: string; descricao: string | null }[];
  crachas: CrachaPbs[];
  souEu: boolean;
  salvando: boolean;
  erro: unknown;
  aoSalvar: (dados: {
    usuario: number;
    ativo: boolean;
    adminMaster: boolean;
    papeis: number[];
    empresas: number[];
    centros: number[];
  }) => void;
  aoFechar: () => void;
}): JSX.Element {
  const [ativo, definirAtivo] = React.useState(acesso.ativo);
  const [adminMaster, definirAdmin] = React.useState(acesso.adminMaster);
  const [meusPapeis, definirPapeis] = React.useState<Set<number>>(new Set(acesso.papeis));
  const [minhasEmpresas, definirEmpresas] = React.useState<Set<number>>(new Set(acesso.empresas));
  const [meusCentros, definirCentros] = React.useState<Set<number>>(new Set(acesso.centros));
  const [verCentros, definirVerCentros] = React.useState(acesso.centros.length > 0);

  const alternar = <T,>(conjunto: Set<T>, valor: T): Set<T> => {
    const proximo = new Set(conjunto);
    if (proximo.has(valor)) proximo.delete(valor);
    else proximo.add(valor);
    return proximo;
  };

  /** Quantos centros este acesso enxerga, contando o que a empresa libera. */
  const alcance = React.useMemo(() => {
    const ids = new Set(meusCentros);
    for (const c of centros) if (minhasEmpresas.has(c.empresa)) ids.add(c.centroEstoque);
    return ids.size;
  }, [meusCentros, minhasEmpresas, centros]);

  const semNada = !adminMaster && minhasEmpresas.size === 0 && meusCentros.size === 0;

  return (
    <Cartao>
      <CabecalhoCartao>
        <div className="min-w-0">
          <TituloCartao>{acesso.nome}</TituloCartao>
          <p className="font-mono text-[11.5px] text-textoFraco">
            {acesso.login} · id {acesso.usuario}
          </p>
        </div>
        <span className="flex items-center gap-1.5">
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoFechar}>
            <X className="h-4 w-4" />
            Fechar
          </Botao>
          <Botao
            variante="primaria"
            tamanho="pequeno"
            carregando={salvando}
            onClick={() =>
              aoSalvar({
                usuario: acesso.usuario,
                ativo,
                adminMaster,
                papeis: [...meusPapeis],
                empresas: [...minhasEmpresas],
                centros: [...meusCentros],
              })
            }
          >
            <Save className="h-4 w-4" />
            Salvar
          </Botao>
        </span>
      </CabecalhoCartao>

      <CorpoCartao className="space-y-5">
        {erro ? <EstadoErro erro={erro} /> : null}

        <section className="flex flex-wrap gap-2">
          <Chave
            marcada={ativo}
            aoMudar={definirAtivo}
            titulo="Pode entrar"
            detalhe="Desmarcado, o login é recusado."
          />
          <Chave
            marcada={adminMaster}
            aoMudar={definirAdmin}
            titulo="Administrador"
            detalhe="Vê todas as empresas e pode dar acesso a outros."
          />
        </section>

        {souEu && !adminMaster ? (
          <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-[12.5px] text-status-atencao">
            Você está tirando o seu próprio acesso de administrador. Se for o último, o sistema
            recusa.
          </p>
        ) : null}

        {/* O crachá do PBS: a identidade que o ERP cobra na operação. */}
        <section>
          <h3 className="rotulo mb-2 flex items-center gap-1.5">
            <BadgeCheck className="h-3.5 w-3.5" />
            Crachá no PBS
          </h3>
          <CrachaDoProcfit crachas={crachas} cracha={acesso.cracha} />
        </section>

        {/* Empresas: a concessão principal. */}
        <section>
          <h3 className="rotulo mb-2 flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5" />
            Empresas que este usuário enxerga
          </h3>
          {adminMaster ? (
            <p className="text-[12.5px] text-textoFraco">
              Administrador enxerga todas as empresas — não há o que marcar aqui.
            </p>
          ) : empresas.length === 0 ? (
            <p className="text-[12.5px] text-textoFraco">Nenhuma empresa disponível.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {empresas.map((e) => {
                const marcada = minhasEmpresas.has(e.empresa);
                return (
                  <li key={e.empresa}>
                    <button
                      type="button"
                      onClick={() => definirEmpresas((a) => alternar(a, e.empresa))}
                      className={cn(
                        'flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[12.5px] transition-colors',
                        marcada
                          ? 'border-primaria bg-primaria-suave font-semibold text-primaria'
                          : 'border-borda bg-superficie hover:bg-superficie2',
                      )}
                    >
                      {marcada ? <Check className="h-3.5 w-3.5" /> : null}
                      <span className="tabular font-bold">{e.empresa}</span>
                      <span className="max-w-[160px] truncate">{e.nome ?? 'sem nome'}</span>
                      <span className="text-[10.5px] text-textoFraco">{e.centros} CDs</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Centros avulsos: a exceção, recolhida. */}
        {!adminMaster ? (
          <section>
            <button
              type="button"
              onClick={() => definirVerCentros((v) => !v)}
              className="rotulo flex items-center gap-1.5 hover:text-texto"
            >
              <Warehouse className="h-3.5 w-3.5" />
              Centros avulsos
              {meusCentros.size > 0 ? (
                <span className="tabular rounded bg-primaria-suave px-1.5 text-[10px] font-bold text-primaria">
                  {meusCentros.size}
                </span>
              ) : null}
              <span className="text-textoSuave">{verCentros ? '−' : '+'}</span>
            </button>
            {verCentros ? (
              <>
                <p className="mb-2 mt-1 text-[11.5px] text-textoFraco">
                  Só para o caso fino: um CD de empresa que este usuário não enxerga inteira. O que
                  as empresas acima liberam já está incluído.
                </p>
                <ul className="grid max-h-[220px] gap-0.5 overflow-y-auto sm:grid-cols-2">
                  {centros.map((c) => {
                    const pelaEmpresa = minhasEmpresas.has(c.empresa);
                    return (
                      <li key={c.centroEstoque}>
                        <label
                          className={cn(
                            'flex min-h-toque cursor-pointer items-center gap-2 rounded px-2 text-[12.5px]',
                            pelaEmpresa ? 'opacity-50' : 'hover:bg-superficie2',
                          )}
                          title={pelaEmpresa ? 'Já liberado pela empresa marcada acima' : undefined}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[rgb(var(--cor-primaria))]"
                            checked={meusCentros.has(c.centroEstoque) || pelaEmpresa}
                            disabled={pelaEmpresa}
                            onChange={() => definirCentros((a) => alternar(a, c.centroEstoque))}
                          />
                          <span className="truncate">
                            <span className="tabular font-semibold">{c.centroEstoque}</span> ·{' '}
                            {c.nome}
                            <span className="ml-1 text-[10.5px] text-textoFraco">
                              (empresa {c.empresa})
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </section>
        ) : null}

        {/* Papéis: o que pode FAZER. */}
        <section>
          <h3 className="rotulo mb-2 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />O que pode fazer
          </h3>
          {adminMaster ? (
            <p className="text-[12.5px] text-textoFraco">
              Administrador tem todas as permissões, independente de papel.
            </p>
          ) : papeis.length === 0 ? (
            <Esqueleto className="h-16" />
          ) : (
            <ul className="grid gap-1.5">
              {papeis.map((p) => {
                const marcado = meusPapeis.has(p.papelId);
                return (
                  <li key={p.papelId}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-start gap-2.5 rounded-lg border px-2.5 py-2 transition-colors',
                        marcado
                          ? 'border-primaria bg-primaria-suave'
                          : 'border-borda hover:bg-superficie2',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 accent-[rgb(var(--cor-primaria))]"
                        checked={marcado}
                        onChange={() => definirPapeis((a) => alternar(a, p.papelId))}
                      />
                      <span className="min-w-0">
                        <span
                          className={cn(
                            'block text-[13px] font-semibold',
                            marcado && 'text-primaria',
                          )}
                        >
                          {p.nome}
                        </span>
                        {p.descricao ? (
                          <span className="block text-[11.5px] text-textoFraco">{p.descricao}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* O resumo do que vai ser salvo, em uma frase. */}
        <p
          className={cn(
            'rounded-lg px-3 py-2 text-[12.5px]',
            semNada
              ? 'bg-status-atencaoFundo text-status-atencao'
              : 'bg-superficie2 text-textoFraco',
          )}
        >
          {!ativo
            ? 'Este usuário não vai conseguir entrar.'
            : adminMaster
              ? 'Administrador: enxerga todas as empresas e todos os centros.'
              : semNada
                ? 'Sem empresa nem centro marcado, este usuário entra e não vê nada.'
                : `Enxerga ${alcance} ${alcance === 1 ? 'centro' : 'centros'}, em ${
                    minhasEmpresas.size
                  } ${minhasEmpresas.size === 1 ? 'empresa' : 'empresas'}${
                    meusCentros.size > 0 ? ` mais ${meusCentros.size} avulso(s)` : ''
                  }.`}
        </p>
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * O crachá que o Procfit tem vinculado a esta pessoa — só leitura.
 *
 * O vínculo é cadastro do Procfit (`dbo.CRACHAS.USUARIO`) e é lá que se muda.
 * O WMS escrevendo nele soltou 22 crachás em 01/10/2026. Quem precisa operar
 * com outro crachá troca na própria tela de operação, só para aquela sessão.
 */
function CrachaDoProcfit({
  crachas,
  cracha,
}: {
  crachas: CrachaPbs[];
  cracha: string | null;
}): JSX.Element {
  const atual = crachas.find((c) => c.cracha === cracha) ?? null;
  if (!cracha) {
    return (
      <p className="text-[12.5px] text-textoFraco">
        Sem crachá vinculado no Procfit. Na conferência e no faturamento este usuário vai informar
        o número na tela — vale só para a sessão dele. Para vir sozinho, vincule no cadastro de
        crachás do Procfit.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-borda bg-superficie2 px-3 py-2">
      <span className="tabular font-mono text-[15px] font-bold">{cracha}</span>
      {atual?.nome ? <span className="text-[12.5px] text-texto">{atual.nome}</span> : null}
      {atual?.impressoraNfe ? (
        <span className="flex items-center gap-1 text-[11.5px] text-textoFraco">
          <Printer className="h-3.5 w-3.5" />
          {atual.impressoraNfe}
        </span>
      ) : null}
      <span className="ml-auto text-[11.5px] text-textoFraco">vinculado no Procfit</span>
    </div>
  );
}

function Chave({
  marcada,
  aoMudar,
  titulo,
  detalhe,
}: {
  marcada: boolean;
  aoMudar: (v: boolean) => void;
  titulo: string;
  detalhe: string;
}): JSX.Element {
  return (
    <label
      className={cn(
        'flex flex-1 cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors',
        marcada ? 'border-primaria bg-primaria-suave' : 'border-borda hover:bg-superficie2',
      )}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-[rgb(var(--cor-primaria))]"
        checked={marcada}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span>
        <span className={cn('block text-[13px] font-semibold', marcada && 'text-primaria')}>
          {titulo}
        </span>
        <span className="block text-[11.5px] text-textoFraco">{detalhe}</span>
      </span>
    </label>
  );
}
